import { gql, useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import { AnyObject } from '../../../models/types';

export interface SnapshotsInfoVars {
  domainIdentifier: string;
  instanceId: string;
}

// Phase 1: list repositories (signed via 'domain.es60.cat.repositories').
const REPOSITORIES_INFO = gql`
  query getRepositoriesInfo(
    $domainIdentifier: String!
    $instanceId: String!
    $piezoArtifacts: JSON
  ) {
    domain(
      domainIdentifier: $domainIdentifier
      instanceId: $instanceId
      piezoArtifacts: $piezoArtifacts
    ) {
      es60 {
        cat {
          repositories
        }
      }
    }
  }
`;

// Phase 2: snapshots for the discovered repo (signed via 'domain.es60.cat.snapshots' using $repo).
const SNAPSHOTS_INFO = gql`
  query getSnapshotsInfo(
    $domainIdentifier: String!
    $instanceId: String!
    $repo: String
    $piezoArtifacts: JSON
  ) {
    domain(
      domainIdentifier: $domainIdentifier
      instanceId: $instanceId
      piezoArtifacts: $piezoArtifacts
    ) {
      es60 {
        cat {
          snapshots(repo: $repo)
        }
      }
    }
  }
`;

interface SnapshotsState {
  loading: boolean;
  data: AnyObject | undefined;
  error: any;
}

/**
 * Two-phase snapshots fetch. The snapshot list lives under a repository whose name is only known
 * after querying _cat/repositories, but Piezo requires the browser to sign the exact tool url up
 * front — so a single server-side call to _cat/snapshots/<repo> cannot be signed. Instead we:
 *   1. fetch repositories (signed), find the cs-automated repo client-side, then
 *   2. fetch snapshots(repo) (signed, url known to the browser).
 *
 * Exposes the same [trigger, { loading, data, error }] tuple as a lazy query, with data shaped as
 * domain.es60.cat.snapshots, so SnapshotsInfo.tsx needs no changes.
 */
export const useSnapshotsInfoQuery = (): [
  (opts: { variables: SnapshotsInfoVars }) => Promise<void>,
  SnapshotsState,
] => {
  const client = useApolloClient();
  const [state, setState] = useState<SnapshotsState>({
    loading: false,
    data: undefined,
    error: undefined,
  });

  const trigger = useCallback(
    async ({ variables }: { variables: SnapshotsInfoVars }) => {
      const { domainIdentifier, instanceId } = variables;
      setState({ loading: true, data: undefined, error: undefined });
      try {
        const repoRes = await client.query({
          query: REPOSITORIES_INFO,
          variables: { domainIdentifier, instanceId },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        const reposRaw = repoRes?.data?.domain?.es60?.cat?.repositories;
        const repositories = Array.isArray(reposRaw)
          ? reposRaw
          : reposRaw && typeof reposRaw === 'object'
            ? [reposRaw]
            : [];
        const csAutomated = repositories.find(
          (item: any) => item && item.id && String(item.id).includes('cs-automated'),
        );
        const repo = csAutomated ? csAutomated.id : '';

        const snapRes = await client.query({
          query: SNAPSHOTS_INFO,
          variables: { domainIdentifier, instanceId, repo },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        setState({
          loading: false,
          data: snapRes?.data,
          error: snapRes?.error,
        });
      } catch (e) {
        setState({ loading: false, data: undefined, error: e });
      }
    },
    [client],
  );

  return [trigger, state];
};
